import type { CSSProperties } from 'react';

import { Group, Stack, Text } from '@mantine/core';
import { IconCalendar, IconListCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { NoteVisibilityBadge } from '@/components/notes/NoteVisibilityBadge';
import { UserAvatar } from '@/components/user/UserAvatar';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import { ICON_SIZE_TAB } from '@/constants/ui';
import type { NoteMapItem } from '@/types/notes';

import styles from './NoteInfoCard.module.css';

const MAX_VISIBLE_LINKED_TASKS = 3;

type NoteInfoCardData = Pick<
  NoteMapItem,
  | 'title'
  | 'color'
  | 'visibility'
  | 'creatorName'
  | 'creatorAvatarUrl'
  | 'createdAt'
  | 'updatedAt'
  | 'linkedTasks'
>;

interface NoteInfoCardProps {
  note: NoteInfoCardData;
}

export function NoteInfoCard({ note }: NoteInfoCardProps) {
  const { t } = useTranslation();
  const { formatDateCompact, formatTime } = useLocalizedDate();

  const linkedTaskCount = note.linkedTasks.length;
  const visibleLinkedTasks = note.linkedTasks.slice(
    0,
    MAX_VISIBLE_LINKED_TASKS
  );
  const hiddenLinkedTaskCount = Math.max(
    0,
    linkedTaskCount - MAX_VISIBLE_LINKED_TASKS
  );
  const noteDate = note.updatedAt ?? note.createdAt;

  return (
    <div className={styles.cardBody}>
      <div className={styles.titleRow}>
        <div className={styles.titleGroup}>
          <div
            className={styles.colorDot}
            style={{ '--note-dot-color': note.color } as CSSProperties}
          />
          <span className={styles.title}>{note.title}</span>
        </div>
        <NoteVisibilityBadge
          visibility={note.visibility}
          className={styles.visibilityBadge}
        />
      </div>

      <div className={styles.authorRow}>
        <div className={styles.authorGroup}>
          <UserAvatar
            displayName={note.creatorName}
            avatarUrl={note.creatorAvatarUrl}
            size="xs"
          />
          <span className={styles.authorName}>{note.creatorName}</span>
        </div>
        <div className={styles.metaGroup}>
          <IconCalendar size={ICON_SIZE_TAB} className={styles.metaIcon} />
          <span className={styles.metaText}>
            {formatDateCompact(noteDate)} {formatTime(noteDate)}
          </span>
        </div>
      </div>

      {linkedTaskCount === 1 && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            {t('notes.hoverCard.linkedTo')}
          </Text>
          <Group gap={4} wrap="nowrap">
            <IconListCheck size={ICON_SIZE_TAB} />
            <Text size="sm" lineClamp={1}>
              {visibleLinkedTasks[0]?.title} ({visibleLinkedTasks[0]?.shortId})
            </Text>
          </Group>
        </Stack>
      )}

      {linkedTaskCount > 1 && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            {t('notes.hoverCard.linkedToMany', { count: linkedTaskCount })}
          </Text>
          <Stack gap={2} className={styles.linkedTasksList}>
            {visibleLinkedTasks.map((task) => (
              <Group key={task.id} gap={4} wrap="nowrap">
                <IconListCheck size={ICON_SIZE_TAB} />
                <Text size="sm" lineClamp={1}>
                  {task.title}
                </Text>
              </Group>
            ))}
          </Stack>
          {hiddenLinkedTaskCount > 0 && (
            <Text size="xs" c="dimmed">
              {t('notes.hoverCard.moreTasks', {
                count: hiddenLinkedTaskCount,
              })}
            </Text>
          )}
        </Stack>
      )}
    </div>
  );
}
